import {
  paymentPurposeLabels,
  transactionKindLabels,
  transactionStatusLabels,
  type TransactionStatus,
} from "@car/api/payments/config";
import { Badge } from "@car/ui/components/badge";
import { Button } from "@car/ui/components/button";
import { Card, CardContent } from "@car/ui/components/card";
import { Skeleton } from "@car/ui/components/skeleton";
import { SEP } from "@car/ui/lib/utils";
import { MoneyReceive02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { PageHeader, PanelEmpty, PanelQueryError } from "@/components/panel-bits";
import { faDate, faDateTimeShort, faPlainNumber, faToman, toFaDigits } from "@/lib/format";
import { PayDialog } from "@/routes/pay/-pay-dialog";
import { client, orpc } from "@/utils/orpc";

export const Route = createFileRoute("/panel/transactions")({
  head: () => ({ meta: [{ title: "تراکنش‌ها | پنل مشتریان تابان خودرو" }] }),
  component: TransactionsPage,
});

type Transactions = Awaited<ReturnType<typeof client.panel.transactions.list>>;
type Transaction = Transactions["transactions"][number];

const statusClass = {
  open: "bg-amber-500/15 text-amber-800 dark:text-amber-400",
  review: "bg-amber-500/15 text-amber-800 dark:text-amber-400",
  paid: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  failed: "bg-destructive/10 text-destructive",
  rejected: "bg-destructive/10 text-destructive",
  cancelled: "bg-muted text-muted-foreground",
  expired: "bg-muted text-muted-foreground",
  refunded: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
} satisfies Record<TransactionStatus, string>;

/** statuses whose note is the reason (the others carry the customer's own note) */
const withReason: readonly TransactionStatus[] = ["failed", "rejected", "refunded", "cancelled"];

/** a pay-link the customer can still pay: waits above, not in the history */
const isOpenLink = (item: Transaction) => item.kind === "link" && item.status === "open";

function TransactionsPage() {
  const query = useQuery(orpc.panel.transactions.list.queryOptions());

  return (
    <div>
      <PageHeader
        title="تراکنش‌ها"
        description="آنچه منتظر پرداخت شماست، لینک‌های پرداختی که کارشناس فروش برایتان فرستاده، و سابقه همه پرداخت‌ها و بیعانه‌های شما."
      />
      {query.isPending ? (
        <div className="grid gap-3">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : query.isError ? (
        <PanelQueryError title="تراکنش‌ها بارگیری نشد" query={query} />
      ) : (
        <div className="grid gap-8">
          <Waiting data={query.data} />
          <History items={query.data.transactions.filter((item) => !isOpenLink(item))} />
        </div>
      )}
    </div>
  );
}

function Waiting({ data }: { data: Transactions }) {
  const links = data.transactions.filter(isOpenLink);
  return (
    <section aria-labelledby="tx-waiting" className="grid gap-3">
      <h2 id="tx-waiting" className="text-base font-black">
        منتظر پرداخت شما
      </h2>
      {links.length + data.dues.length === 0 ? (
        <p className="rounded-xl border border-dashed p-4 text-center text-sm text-muted-foreground">
          موردی منتظر پرداخت شما نیست.
        </p>
      ) : null}
      {links.map((link) => (
        <Card key={link.id}>
          <CardContent className="flex flex-wrap items-center justify-between gap-3">
            <div className="grid gap-0.5">
              <span className="font-bold">{link.title}</span>
              <span className="text-sm text-muted-foreground">
                {link.methodLabel}
                {link.expiresIso ? `${SEP}مهلت پرداخت تا ${faDateTimeShort(link.expiresIso)}` : ""}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-black">{faToman(link.amount)}</span>
              {link.token ? (
                <Button
                  size="sm"
                  nativeButton={false}
                  render={<Link to="/d/$token" params={{ token: link.token }} />}
                >
                  پرداخت آنلاین
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ))}
      {data.dues.map((due) => (
        <Card key={`${due.purpose}:${due.targetId}`}>
          <CardContent className="flex flex-wrap items-center justify-between gap-3">
            <div className="grid gap-0.5">
              <span className="font-bold">{due.title}</span>
              <span className="text-sm text-muted-foreground">
                {paymentPurposeLabels[due.purpose]}
                {SEP}
                {faDate(due.dateIso)}
                {due.paid > 0
                  ? `${SEP}پرداخت‌شده ${faToman(due.paid)} از ${faToman(due.total)}`
                  : ""}
              </span>
              {due.inReview > 0 ? (
                <span className="text-xs text-amber-800 dark:text-amber-400">
                  {faToman(due.inReview)} در انتظار تأیید کارشناس صندوق
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-black">{faToman(due.due)}</span>
              <PayDialog purpose={due.purpose} targetId={due.targetId} title={due.title}>
                پرداخت
              </PayDialog>
            </div>
          </CardContent>
        </Card>
      ))}
    </section>
  );
}

function History({ items }: { items: Transaction[] }) {
  return (
    <section aria-labelledby="tx-history" className="grid gap-3">
      <h2 id="tx-history" className="text-base font-black">
        سابقه پرداخت‌ها
      </h2>
      {items.length === 0 ? (
        <PanelEmpty
          icon={MoneyReceive02Icon}
          title="هنوز تراکنشی ثبت نشده"
          description="پرداخت فاکتور، سفارش و قسط و بیعانه‌ای که در شعبه می‌دهید، اینجا می‌آید."
        />
      ) : (
        <ul className="grid gap-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-card px-4 py-3 text-sm"
            >
              <span className="grid gap-0.5">
                <span className="font-bold">{item.title}</span>
                <span className="text-xs text-muted-foreground">
                  {transactionKindLabels[item.kind]}
                  {SEP}
                  {item.methodLabel}
                  {SEP}
                  {faDate(item.dateIso)}
                </span>
                {item.receiptNumber !== null ? (
                  <span className="text-xs text-muted-foreground">
                    رسید شماره {faPlainNumber(item.receiptNumber)}
                    {item.applied ? `${SEP}به سفارش شما افزوده شد` : ""}
                  </span>
                ) : null}
                {item.reference ? (
                  <span className="text-xs text-muted-foreground">
                    کد پیگیری{" "}
                    <span dir="ltr" className="font-mono">
                      {toFaDigits(item.reference)}
                    </span>
                  </span>
                ) : null}
                {item.status === "expired" && item.expiresIso ? (
                  <span className="text-xs text-muted-foreground">
                    مهلت پرداخت {faDateTimeShort(item.expiresIso)} تمام شد؛ لینک تازه را از کارشناس
                    فروش بخواهید.
                  </span>
                ) : null}
                {item.note ? (
                  <span
                    className={
                      withReason.includes(item.status)
                        ? "text-xs text-destructive"
                        : "text-xs text-muted-foreground"
                    }
                  >
                    {withReason.includes(item.status) ? `علت: ${item.note}` : item.note}
                  </span>
                ) : null}
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-black">{faToman(item.amount)}</span>
                {item.sandbox ? <Badge variant="outline">آزمایشی</Badge> : null}
                <Badge className={statusClass[item.status]}>
                  {transactionStatusLabels[item.status]}
                </Badge>
                {item.kind === "payment" && (item.status === "paid" || item.status === "review") ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    nativeButton={false}
                    render={
                      <Link to="/pay/callback/$paymentId" params={{ paymentId: item.sourceId }} />
                    }
                  >
                    رسید
                  </Button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
